(function () {
  const config = JSON.parse(
    document.getElementById("OrderQuantityConfig").textContent,
  );
  const overrides = new Map();
  const requests = new Map();
  const reservations = new Map();
  let cart = config.cart;
  let refreshVersion = 0;

  const isSubscription = (item) =>
    Boolean(item.selling_plan_allocation || item.selling_plan);
  const variantId = (item) => String(item.variant_id || item.id);
  const positiveInteger = (value) =>
    (typeof value === "number" || typeof value === "string") &&
    Number.isSafeInteger(Number(value)) && Number(value) > 0;

  function configuredMaximum(value, name) {
    if (value == null || (typeof value === "string" && value.trim() === "")) return Infinity;
    if (!positiveInteger(value)) {
      throw new Error(`The ${name} maximum order quantity must be a positive integer or blank.`);
    }
    return Number(value);
  }
  const globalMaximum = configuredMaximum(config.maximum, "one-time");
  const subscriptionMaximum = configuredMaximum(config.subscriptionMaximum, "subscription");

  function register(limits) {
    Object.entries(limits).forEach(([id, value]) => {
      if (value !== null && !positiveInteger(value)) {
        console.warn(`Invalid custom.order_max_quantity for variant ${id}:`, value);
      }
      overrides.set(id, positiveInteger(value) ? Number(value) : null);
    });
  }

  function maximum(id, subscription = false) {
    return subscription ? subscriptionMaximum : overrides.get(String(id)) ?? globalMaximum;
  }

  // Purchase types have separate allowances; all subscription selling plans share one.
  function quantity(id, excludingKey, subscription = false) {
    const reserved = [...reservations.values()].reduce(
      (total, item) => total + (
        item.id === String(id) && item.subscription === subscription ? item.quantity : 0
      ),
      0,
    );
    return cart.items.reduce(
      (total, item) =>
        isSubscription(item) === subscription &&
        variantId(item) === String(id) &&
        item.key !== excludingKey
          ? total + item.quantity
          : total,
      reserved,
    );
  }

  function remaining(id, excludingKey, subscription = false) {
    return Math.max(0, maximum(id, subscription) - quantity(id, excludingKey, subscription));
  }

  function message(id, subscription = false) {
    const max = maximum(id, subscription);
    if (!Number.isFinite(max)) return config.error;
    return (subscription ? config.subscriptionMessage : config.message).replace("[max]", max);
  }

  function evaluateAdd(id, amount, subscription = false) {
    // Unknown one-time variants may have overrides even when the global limit is blank.
    const known = subscription || overrides.has(String(id));
    const available = known ? remaining(id, undefined, subscription) : 0;
    const blocked = !positiveInteger(amount) || Number(amount) > available;
    return {
      blocked, remaining: available,
      message: blocked ? (known ? message(id, subscription) : config.error) : false,
    };
  }

  function lineForRow(row) {
    return cart.items.find((item) => item.key === row.dataset.itemKey);
  }

  function lineLimit(item) {
    const subscription = isSubscription(item);
    const known = subscription || overrides.has(variantId(item));
    return {
      editable: !subscription,
      // Unknown app-added variants stay locked until their overrides have loaded.
      maximum: known ? remaining(variantId(item), item.key, subscription) : 0,
      message: known ? message(variantId(item), subscription) : config.error,
    };
  }

  function updateLine(row, item) {
    if (!item) return;
    const limit = lineLimit(item);
    if (!limit.editable) return;
    const inputs = row.querySelectorAll(
      '.hs-cart-plus-minus input, input.update-cart--template, input[name="updates[]"]',
    );
    const max = limit.maximum;
    inputs.forEach((input) => {
      if (input.type !== "hidden") {
        if (Number.isFinite(max)) input.max = String(max);
        else input.removeAttribute("max");
      }
    });
    row.querySelectorAll(".hs-inc, [name='plus']").forEach((button) => {
      const input = button.closest("quantity-input, .hs-cart-plus-minus")?.querySelector("input");
      if (!input || input.type === "hidden") return;
      const disabled = Number(input.value) >= max;
      button.setAttribute("aria-disabled", String(disabled));
      if (button.tagName === "BUTTON") button.disabled = disabled;
    });
  }

  function render() {
    document.querySelectorAll(
      ".cart-product-item[data-item-key], .hs-sticky-cart .hs-item-cart-content[data-item-key]",
    ).forEach((row) => updateLine(row, lineForRow(row)));
  }

  function notify() {
    render();
    document.dispatchEvent(new CustomEvent("order-quantity:updated"));
  }

  function loadProduct(handle) {
    if (!requests.has(handle)) {
      const root = window.Shopify?.routes?.root || "/";
      const request = fetch(`${root}products/${encodeURIComponent(handle)}?view=metafields`)
        .then((response) => {
          if (!response.ok) throw new Error(`Quantity metadata failed: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          if (!data.order_quantity_limits) {
            throw new Error(`Quantity metadata missing for product ${handle}`);
          }
          register(data.order_quantity_limits);
        })
        .catch((error) => {
          requests.delete(handle);
          throw error;
        });
      requests.set(handle, request);
    }
    return requests.get(handle);
  }

  function setCart(nextCart) {
    refreshVersion++;
    cart = nextCart;
    notify();
    const handles = new Set(
      cart.items
        .filter((item) => !isSubscription(item) && !overrides.has(variantId(item)))
        .map((item) => item.handle),
    );
    if (handles.size) {
      Promise.all([...handles].map(loadProduct))
        .then(notify)
        .catch((error) => {
          console.error("Unable to load order quantity limits", error);
          document.querySelectorAll(
            ".hs-sticky-cart .hs-item-cart-content[data-item-key]",
          ).forEach((row) => {
            const item = lineForRow(row);
            if (item && !isSubscription(item) && !overrides.has(variantId(item))) {
              showError(row, config.error);
            }
          });
        });
    }
  }

  function showError(row, text) {
    let error = row.querySelector("[data-order-quantity-error]");
    if (!error) {
      error = document.createElement("small");
      error.setAttribute("data-order-quantity-error", "");
      error.setAttribute("role", "alert");
      // Keep alerts inside product details, not beside the row's flex columns.
      const content = row.querySelector(".cart--info, .hs-item-cart-description") || row;
      content.append(error);
    }
    error.textContent = text;
  }

  function guardCartControl(event) {
    const row = event.target.closest(
      ".cart-product-item[data-item-key], .hs-sticky-cart .hs-item-cart-content[data-item-key]",
    );
    if (!row) return;
    const item = lineForRow(row);
    if (!item) return;
    const limit = lineLimit(item);
    if (!limit.editable) return;
    const plus = event.target.closest(".hs-inc, [name='plus']");
    if (event.type === "keydown" && !["Enter", " "].includes(event.key)) return;
    const isChange =
      event.type === "change" &&
      event.target.matches(".hs-cart-plus-minus input, input.update-cart--template");
    if (!plus && !isChange) return;
    // Mobile and desktop cart controls coexist; validate the control that actually changed.
    const input = isChange
      ? event.target
      : plus.closest("quantity-input, .hs-cart-plus-minus")?.querySelector("input");
    if (!input || input.type === "hidden") return;
    const requested = Number(input.value) + (plus ? 1 : 0);
    const max = limit.maximum;
    // Existing over-limit carts may be reduced or removed, never increased.
    if (
      input.value.trim() === "" ||
      !Number.isSafeInteger(requested) ||
      requested < 0 ||
      (requested > max && requested >= item.quantity)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (isChange) input.value = String(item.quantity);
      showError(row, limit.message);
    } else {
      const error = row.querySelector("[data-order-quantity-error]");
      if (error) error.textContent = "";
    }
  }

  register(config.limits);
  window.orderQuantityLimits = {
    register,
    lineForRow,
    lineLimit,
    evaluateAdd,
    maximum,
    quantity,
    remaining,
    message,
    setCart,
    updateLine,
    reserve(id, amount, subscription = false) {
      const state = evaluateAdd(id, amount, subscription);
      if (state.blocked) throw new Error(state.message);
      const token = Symbol();
      reservations.set(token, { id: String(id), quantity: Number(amount), subscription });
      notify();
      return token;
    },
    release(token) {
      reservations.delete(token);
      notify();
    },
    // Update immediately after a successful add, before the cart refresh completes.
    recordAdd(item) {
      refreshVersion++;
      const existing = cart.items.find((line) => line.key === item.key);
      cart = {
        ...cart,
        // A cart watcher may already have fetched this line; replace it instead of double-counting.
        items: [
          ...cart.items.filter((line) => line.key !== item.key),
          { ...item, quantity: Math.max(item.quantity, existing?.quantity || 0) },
        ],
      };
      notify();
    },
  };
  document.addEventListener("click", guardCartControl, true);
  document.addEventListener("change", guardCartControl, true);
  document.addEventListener("keydown", guardCartControl, true);
  document.addEventListener("cart:updated", async () => {
    const version = ++refreshVersion;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`);
      if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
      const nextCart = await response.json();
      if (version === refreshVersion) setCart(nextCart);
    } catch (error) {
      console.error("Unable to refresh order quantity limits", error);
    }
  });
  render();
})();
